import { differenceInDays, parseISO } from 'date-fns'

import { AuctionAsset, ServiceStatusEnum } from 'src/generated-unified/graphql'

export type filterPsiVehiclesResult = {
    eligible: AuctionAsset[]
    ordered: AuctionAsset[]
    completed: AuctionAsset[]
}

const fourDaysAgo = new Date(new Date().setHours(0, 0, 0, 0))
fourDaysAgo.setDate(fourDaysAgo.getDate() - 4)

const hasInspectionOptions = (item: AuctionAsset): boolean => Boolean(item?.inspection?.options?.length)
export const hasNoServiceOrders = (item: AuctionAsset) => item?.serviceOrders?.orders?.length === 0
export const hasEligibleDate = (item: AuctionAsset): boolean => {
    const saleDate = item?.saleListingSaleDate
    if (!saleDate) return false

    const vehicleSaleDate = parseISO(saleDate)
    const daysDiff = differenceInDays(new Date(), vehicleSaleDate)

    return daysDiff <= 2
}

// Only consider non-PRE_SALE_INSPECTION orders when evaluating status buckets
const hasStatusExcludingPreSale = (item: AuctionAsset, statuses: ServiceStatusEnum[]) =>
    item?.serviceOrders?.orders?.some(
        (order) => order?.class !== 'PRE_SALE_INSPECTION' && order?.status && statuses.includes(order?.status),
    )

const hasOrderedOrInProgressOrders = (item: AuctionAsset) =>
    hasStatusExcludingPreSale(item, [ServiceStatusEnum.Ordered, ServiceStatusEnum.InProgress])

const hasCompletedOrFailedOrders = (item: AuctionAsset) =>
    hasStatusExcludingPreSale(item, [ServiceStatusEnum.Completed, ServiceStatusEnum.Failed])

const isRecentSale = (item: AuctionAsset) =>
    item?.saleListingSaleDate && new Date(new Date(item?.saleListingSaleDate).setHours(0, 0, 0, 0)) >= fourDaysAgo

export const filterPsiVehicles = (items: AuctionAsset[]): filterPsiVehiclesResult => {
    if (!items)
        return {
            eligible: [],
            ordered: [],
            completed: [],
        }

    const hasOnlyCancelledOrPreSaleOrders = (item: AuctionAsset): boolean => {
        const orders = item?.serviceOrders?.orders || []
        if (orders.length === 0) return false
        return orders.every((o) => o?.class === 'PRE_SALE_INSPECTION' || o?.status === ServiceStatusEnum.Cancelled)
    }

    return items.reduce(
        (acc: filterPsiVehiclesResult, item) => {
            if (
                hasInspectionOptions(item) &&
                (hasNoServiceOrders(item) || hasOnlyCancelledOrPreSaleOrders(item)) &&
                hasEligibleDate(item)
            ) {
                acc.eligible.push(item)
            } else if (hasOrderedOrInProgressOrders(item)) {
                acc.ordered.push(item)
            } else if (isRecentSale(item) && hasCompletedOrFailedOrders(item)) {
                acc.completed.push(item)
            }
            return acc
        },
        { eligible: [], ordered: [], completed: [] },
    )
}
